<template>
  <div class="invoices-index-page">
    <!-- Simple Filters Section -->
    <b-card class="mb-3">
      <b-card-body class="p-1">
        <b-row>
          <b-col
            lg="3"
            md="6"
          >
            <b-form-group
              label="Référence facture"
              label-for="reference"
            >
              <b-form-input
                v-model="filters.reference"
                v-debounce:500ms="debouncedReferenceSearch"
                placeholder="Ex: 1375, 1374..."
                @input="filters.reference = $event"
              />
            </b-form-group>
          </b-col>

          <b-col
            v-if="user.role === 'admin' || user.role === 'manager'"
            lg="3"
            md="6"
          >
            <b-form-group
              label="Fournisseur"
              label-for="supplier"
            >
              <v-select
                v-model="filters.supplier"
                :clearable="true"
                placeholder="Sélectionner un fournisseur"
                label="username"
                :options="suppliers"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            v-if="user.role === 'admin'"
            lg="3"
            md="6"
          >
            <b-form-group
              label="Dépôt"
              label-for="repository"
            >
              <v-select
                v-model="filters.repository"
                :clearable="true"
                placeholder="Sélectionner un dépôt"
                label="name"
                :options="repositories"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            lg="3"
            md="6"
          >
            <b-form-group
              label="Date de début"
              label-for="dateFrom"
            >
              <b-form-datepicker
                v-model="filters.dateFrom"
                v-bind="datePickerConfig"
                placeholder="Sélectionner une date"
                reset-button
                close-button
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            lg="3"
            md="6"
          >
            <b-form-group
              label="Date de fin"
              label-for="dateTo"
            >
              <b-form-datepicker
                v-model="filters.dateTo"
                v-bind="datePickerConfig"
                placeholder="Sélectionner une date"
                reset-button
                close-button
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            lg="3"
            md="6"
          >
            <b-form-group
              label="Montant minimum"
              label-for="totalMin"
            >
              <b-form-input
                v-model="filters.totalMin"
                type="number"
                step="0.001"
                placeholder="0.000"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            lg="3"
            md="6"
          >
            <b-form-group
              label="Montant maximum"
              label-for="totalMax"
            >
              <b-form-input
                v-model="filters.totalMax"
                type="number"
                step="0.001"
                placeholder="0.000"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            lg="3"
            md="6"
          >
            <b-form-group
              label="Colis livrés (minimum)"
              label-for="deliveredMin"
            >
              <b-form-input
                v-model="filters.deliveredMin"
                type="number"
                placeholder="Ex: 10"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
          <small class="text-muted">
            {{ hasActiveFilters ? `${activeFiltersCount} filtres appliqués` : 'Aucun filtre actif' }}
          </small>
          <div>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="mr-2"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            >
              <feather-icon
                icon="XIcon"
                size="14"
                class="mr-1"
              />
              Réinitialiser
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              :disabled="isLoading || selectedInvoiceIds.length === 0 || isExporting"
              @click="exportData"
            >
              <feather-icon
                icon="DownloadIcon"
                size="14"
                class="mr-1"
              />
              Exporter CSV
            </b-button>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Header & KPIs -->
    <b-row class="align-items-center mb-2">
      <b-col>
        <h4 class="mb-0 d-flex align-items-center">
          <feather-icon
            icon="FileTextIcon"
            class="mr-1"
          />
          Liste des factures fournisseurs ({{ count }})
        </h4>
        <small class="text-muted">Vue consolidée avec statistiques clés</small>
      </b-col>
      <b-col class="d-flex justify-content-end align-items-center">
        <b-button
          variant="dark"
          class="mr-1 btn-black"
          @click="openInvoiceStatistics"
        >
          <feather-icon icon="TrendingUpIcon" />
          <span class="ml-1">Statistiques factures</span>
        </b-button>
        <b-button
          v-if="user.role === 'admin'"
          :to="{ name: 'invoice-add' }"
          variant="gradient-primary"
          aria-label="Créer une nouvelle facture fournisseur"
        >
          <feather-icon icon="PlusIcon" />
          <span class="ml-1">Nouvelle facture</span>
        </b-button>
      </b-col>
    </b-row>

    <b-sidebar
      id="invoice-statistics-sidebar"
      v-model="showStatsSection"
      title="Statistiques factures"
      right
      shadow
      width="720px"
      backdrop
    >
      <template #header="{ hide }">
        <div class="d-flex align-items-center justify-content-between w-100 p-3">
          <h4 class="mb-0 d-flex align-items-center">
            <feather-icon
              icon="TrendingUpIcon"
              size="24"
              class="mr-2 text-primary"
            />
            Tableau de bord factures
          </h4>
          <b-button
            variant="link"
            class="text-dark"
            @click="hide"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>

      <div class="px-3 pb-3">
        <div
          v-if="invoiceStatsLoading"
          class="text-center py-5"
        >
          <b-spinner
            variant="primary"
            label="Chargement..."
          />
          <p class="mt-3 text-muted">
            Chargement des statistiques des factures...
          </p>
        </div>

        <b-alert
          v-else-if="invoiceStatsError"
          variant="danger"
          show
          class="mb-3"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <feather-icon
                icon="AlertTriangleIcon"
                class="mr-1"
              />
              {{ invoiceStatsError }}
            </div>
            <b-button
              size="sm"
              variant="outline-light"
              @click="loadInvoiceStatistics"
            >
              <feather-icon icon="RefreshCwIcon" />
              <span class="ml-1">Réessayer</span>
            </b-button>
          </div>
        </b-alert>

        <div v-else-if="invoiceStats">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="mb-0 d-flex align-items-center">
              <feather-icon
                icon="CalendarIcon"
                size="18"
                class="mr-1 text-primary"
              />
              Statistiques par période
            </h5>
            <b-button-group size="sm">
              <b-button
                v-for="option in timeframeOptions"
                :key="option.value"
                :variant="selectedTimeframe === option.value ? 'primary' : 'outline-primary'"
                @click="selectedTimeframe = option.value"
              >
                {{ option.label }}
              </b-button>
            </b-button-group>
          </div>

          <b-row class="mb-3">
            <b-col
              md="4"
              class="mb-2"
            >
              <b-card class="kpi-tile bg-light-primary h-100">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="kpi-label mb-1">
                      Total factures
                    </p>
                    <h3 class="kpi-value mb-0">
                      {{ selectedStats.overview.total_invoices }}
                    </h3>
                  </div>
                  <feather-icon
                    icon="FileTextIcon"
                    class="kpi-icon text-primary"
                  />
                </div>
              </b-card>
            </b-col>
            <b-col
              md="4"
              class="mb-2"
            >
              <b-card class="kpi-tile bg-light-success h-100">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="kpi-label mb-1">
                      Montant TTC
                    </p>
                    <h3 class="kpi-value mb-0">
                      {{ formatStatsCurrency(selectedStats.overview.total_ttc) }}
                    </h3>
                  </div>
                  <feather-icon
                    icon="DollarSignIcon"
                    class="kpi-icon text-success"
                  />
                </div>
              </b-card>
            </b-col>
            <b-col
              md="4"
              class="mb-2"
            >
              <b-card class="kpi-tile bg-light-warning h-100">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="kpi-label mb-1">
                      En attente
                    </p>
                    <h3 class="kpi-value mb-0">
                      {{ selectedStats.payment_status.pending_count }}
                    </h3>
                    <small class="text-muted">Encours {{ formatStatsCurrency(selectedStats.payment_status.outstanding_total_ttc) }}</small>
                  </div>
                  <feather-icon
                    icon="ClockIcon"
                    class="kpi-icon text-warning"
                  />
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col
              md="6"
              class="mb-2"
            >
              <b-card class="kpi-tile bg-light-primary">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="kpi-label mb-1">
                      Total factures
                    </p>
                    <h3 class="kpi-value">
                      {{ selectedStats.overview.total_invoices }}
                    </h3>
                  </div>
                  <feather-icon
                    icon="FileTextIcon"
                    class="kpi-icon text-primary"
                  />
                </div>
              </b-card>
            </b-col>
            <b-col
              md="6"
              class="mb-2"
            >
              <b-card class="kpi-tile bg-light-success">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="kpi-label mb-1">
                      Montant TTC
                    </p>
                    <h3 class="kpi-value">
                      {{ formatStatsCurrency(selectedStats.overview.total_ttc) }}
                    </h3>
                  </div>
                  <feather-icon
                    icon="DollarSignIcon"
                    class="kpi-icon text-success"
                  />
                </div>
              </b-card>
            </b-col>
            <b-col
              md="6"
              class="mb-2"
            >
              <b-card class="kpi-tile bg-light-warning">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="kpi-label mb-1">
                      Factures validées
                    </p>
                    <h3 class="kpi-value">
                      {{ selectedStats.payment_status.validated_count }}
                    </h3>
                    <small class="text-muted">{{ selectedStats.payment_status.validation_rate }}% validées</small>
                  </div>
                  <feather-icon
                    icon="CheckCircleIcon"
                    class="kpi-icon text-warning"
                  />
                </div>
              </b-card>
            </b-col>
            <b-col
              md="6"
              class="mb-2"
            >
              <b-card class="kpi-tile bg-light-danger">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="kpi-label mb-1">
                      En attente
                    </p>
                    <h3 class="kpi-value">
                      {{ selectedStats.payment_status.pending_count }}
                    </h3>
                    <small class="text-muted">Encours {{ formatStatsCurrency(selectedStats.payment_status.outstanding_total_ttc) }}</small>
                  </div>
                  <feather-icon
                    icon="ClockIcon"
                    class="kpi-icon text-danger"
                  />
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col
              md="4"
              class="mb-2"
            >
              <b-card body-class="p-3">
                <h6 class="mb-1 d-flex align-items-center">
                  <feather-icon
                    icon="ActivityIcon"
                    size="16"
                    class="mr-1 text-primary"
                  />
                  Performance livraison
                </h6>
                <p class="mb-1">
                  <strong>{{ selectedStats.overview.delivered_orders }}</strong> colis livrés
                </p>
                <p class="mb-1">
                  <strong>{{ selectedStats.overview.return_orders }}</strong> retours
                </p>
                <p class="mb-0 text-muted">
                  Taux de retour : {{ selectedStats.overview.return_rate }}%
                </p>
              </b-card>
            </b-col>
            <b-col
              md="4"
              class="mb-2"
            >
              <b-card body-class="p-3">
                <h6 class="mb-1 d-flex align-items-center">
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="16"
                    class="mr-1 text-success"
                  />
                  Indicateurs financiers
                </h6>
                <p class="mb-1">
                  Panier moyen : <strong>{{ formatStatsCurrency(selectedStats.financials.average_invoice_value) }}</strong>
                </p>
                <p class="mb-1">
                  Marge moyenne : <strong>{{ selectedStats.financials.average_margin_rate }}%</strong>
                </p>
                <p class="mb-0">
                  Valeur moyenne par colis : <strong>{{ formatStatsCurrency(selectedStats.financials.average_order_value) }}</strong>
                </p>
              </b-card>
            </b-col>
            <b-col
              md="4"
              class="mb-2"
            >
              <b-card body-class="p-3">
                <h6 class="mb-1 d-flex align-items-center">
                  <feather-icon
                    icon="WatchIcon"
                    size="16"
                    class="mr-1 text-warning"
                  />
                  Paiement
                </h6>
                <p class="mb-1">
                  Délai moyen validation : <strong>{{ selectedStats.payment_status.avg_validation_delay_days }} j</strong>
                </p>
                <p class="mb-0 text-muted">
                  Montant HT : {{ formatStatsCurrency(selectedStats.overview.total_ht) }}
                </p>
              </b-card>
            </b-col>
          </b-row>

          <b-row
            v-if="showLeaderboards"
            class="mb-3"
          >
            <b-col
              md="6"
              class="mb-2"
            >
              <b-card body-class="p-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">
                    Top fournisseurs
                  </h6>
                  <feather-icon
                    icon="UsersIcon"
                    size="16"
                    class="text-primary"
                  />
                </div>
                <div v-if="selectedStats.top_suppliers.length">
                  <div
                    v-for="supplier in selectedStats.top_suppliers"
                    :key="supplier.name"
                    class="leaderboard-row"
                  >
                    <div>
                      <strong>{{ supplier.name }}</strong>
                      <div class="text-muted small">
                        {{ supplier.invoices_count }} factures
                      </div>
                    </div>
                    <div class="text-right">
                      <div>{{ formatStatsCurrency(supplier.total_ttc) }}</div>
                      <small class="text-danger">En attente {{ formatStatsCurrency(supplier.pending_ttc) }}</small>
                    </div>
                  </div>
                </div>
                <p
                  v-else
                  class="text-muted mb-0"
                >
                  Aucune donnée disponible.
                </p>
              </b-card>
            </b-col>
            <b-col
              md="6"
              class="mb-2"
            >
              <b-card body-class="p-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">
                    Top dépôts
                  </h6>
                  <feather-icon
                    icon="HomeIcon"
                    size="16"
                    class="text-info"
                  />
                </div>
                <div v-if="selectedStats.top_repositories.length">
                  <div
                    v-for="repository in selectedStats.top_repositories"
                    :key="repository.name"
                    class="leaderboard-row"
                  >
                    <div>
                      <strong>{{ repository.name }}</strong>
                      <div class="text-muted small">
                        {{ repository.invoices_count }} factures
                      </div>
                    </div>
                    <div class="text-right">
                      <div>{{ formatStatsCurrency(repository.total_ttc) }}</div>
                      <small class="text-muted">{{ repository.delivered_orders }} livrés · {{ repository.return_orders }} retours</small>
                    </div>
                  </div>
                </div>
                <p
                  v-else
                  class="text-muted mb-0"
                >
                  Aucune donnée disponible.
                </p>
              </b-card>
            </b-col>
          </b-row>

          <b-card body-class="p-0">
            <div class="p-3 border-bottom d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                Factures récentes
              </h6>
              <feather-icon
                icon="ClockIcon"
                size="16"
                class="text-secondary"
              />
            </div>
            <div v-if="selectedStats.recent_invoices.length">
              <div
                v-for="invoice in selectedStats.recent_invoices"
                :key="invoice.id"
                class="recent-invoice-row"
              >
                <div>
                  <strong>#{{ invoice.id }}</strong>
                  <div class="text-muted small">
                    {{ invoice.created_at_formatted }}
                  </div>
                </div>
                <div class="text-right">
                  <div>{{ formatStatsCurrency(invoice.total_ttc) }}</div>
                  <small :class="invoice.is_payment_validated ? 'text-success' : 'text-warning'">
                    {{ invoice.is_payment_validated ? 'Validée' : 'En attente' }}
                  </small>
                </div>
              </div>
            </div>
            <p
              v-else
              class="text-center text-muted py-3 mb-0"
            >
              Aucune facture récente.
            </p>
          </b-card>
        </div>

        <div
          v-else
          class="text-center text-muted py-5"
        >
          Aucune statistique disponible pour le moment.
        </div>
      </div>
    </b-sidebar>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
      <p class="mt-2 text-muted">
        Chargement des factures...
      </p>
    </div>

    <!-- Empty State - No Data -->
    <b-card
      v-else-if="!isLoading && invoices.length === 0 && !hasActiveFilters"
      class="text-center"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="FileTextIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h4 class="text-muted">
          Aucune facture
        </h4>
        <p class="text-muted mb-3">
          Il n'y a pas encore de factures dans le système.
        </p>
        <b-button
          v-if="user.role === 'admin'"
          :to="{ name: 'invoice-add' }"
          variant="primary"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-1"
          />
          Créer la première facture
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Empty State - No Results -->
    <b-card
      v-else-if="!isLoading && invoices.length === 0 && hasActiveFilters"
      class="text-center no-results-card"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="SearchIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h4 class="text-muted">
          Aucun résultat trouvé
        </h4>
        <p class="text-muted mb-3">
          Aucune facture ne correspond aux critères de recherche sélectionnés.
        </p>
        <b-button
          variant="outline-primary"
          @click="clearFilters"
        >
          <feather-icon
            icon="RefreshCwIcon"
            class="mr-1"
          />
          Réinitialiser les filtres
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Data Table -->
    <b-card
      v-else
      class="shadow-sm"
    >
      <b-alert
        v-if="user.role === 'supplier' && pendingSupplierInvoices.length"
        variant="warning"
        show
        class="supplier-validation-alert mx-1 mb-2"
      >
        <div class="supplier-validation-alert__content">
          <div>
            <strong>{{ pendingSupplierInvoices.length }} facture{{ pendingSupplierInvoices.length > 1 ? 's' : '' }} en attente de validation</strong>
            <div class="small">
              Montant net à confirmer: {{ formatPrice(pendingSupplierNetTotal) }} TND
            </div>
          </div>
          <b-button
            variant="success"
            size="sm"
            :to="{ name: 'invoice-show', params: { id: pendingSupplierInvoices[0].id } }"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="14"
              class="mr-1"
            />
            Vérifier
          </b-button>
        </div>
      </b-alert>

      <div
        v-if="user.role === 'supplier'"
        class="supplier-invoice-cards d-lg-none"
      >
        <div
          v-for="invoice in invoices"
          :key="invoice.id"
          class="supplier-invoice-card"
          :class="{ 'is-pending': !invoice.is_payment_validated }"
        >
          <div class="supplier-invoice-card__header">
            <div>
              <strong class="invoice-ref">Facture #{{ invoice.id }}</strong>
              <div class="text-muted small">
                {{ formatDate(invoice.created_at) }}
              </div>
            </div>
            <b-badge
              :variant="invoice.is_payment_validated ? 'success' : 'warning'"
            >
              {{ invoice.is_payment_validated ? 'Validée' : 'À valider' }}
            </b-badge>
          </div>

          <div class="supplier-invoice-card__amount">
            <span>Net à payer</span>
            <strong>{{ formatPrice(invoice.net_a_payer) }} TND</strong>
          </div>

          <div class="supplier-invoice-card__stats">
            <div>
              <span>Livres</span>
              <strong>{{ invoice.nb_delivered || 0 }}</strong>
            </div>
            <div>
              <span>Retours</span>
              <strong>{{ invoice.nb_return || 0 }}</strong>
            </div>
            <div>
              <span>Total TTC</span>
              <strong>{{ formatPrice(invoice.total_ttc) }}</strong>
            </div>
          </div>

          <div class="supplier-invoice-card__actions">
            <b-button
              :to="{ name: 'invoice-show', params: { id: invoice.id } }"
              variant="outline-primary"
              size="sm"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
                class="mr-1"
              />
              Détails
            </b-button>
            <b-button
              v-if="!invoice.is_payment_validated"
              size="sm"
              variant="success"
              :disabled="invoice._isValidating"
              @click="confirmValidatePayment(invoice)"
            >
              <b-spinner
                v-if="invoice._isValidating"
                small
                type="border"
                class="mr-1"
              />
              <feather-icon
                v-else
                icon="CheckCircleIcon"
                size="14"
                class="mr-1"
              />
              Valider le paiement
            </b-button>
            <b-button
              v-else
              size="sm"
              variant="outline-success"
              disabled
            >
              <feather-icon
                icon="CheckIcon"
                size="14"
                class="mr-1"
              />
              Paiement validé
            </b-button>
          </div>
        </div>
      </div>

      <div
        class="d-flex justify-content-between align-items-center mb-2 px-1 table-toolbar"
        :class="{ 'd-none d-lg-flex': user.role === 'supplier' }"
      >
        <small class="text-muted">
          Sélectionnées: {{ selectedInvoiceIds.length }}
        </small>
        <div>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            :disabled="invoices.length === 0"
            @click="selectAllVisible"
          >
            Tout sélectionner (page)
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="selectedInvoiceIds.length === 0"
            @click="clearSelection"
          >
            Effacer
          </b-button>
        </div>
      </div>
      <vue-good-table
        :class="{ 'd-none d-lg-block': user.role === 'supplier' }"
        :columns="tableColumns"
        :rows="invoices"
        :search-options="{ enabled: false }"
        :pagination-options="{ enabled: false }"
        style-class="vgt-table striped"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >
          <!-- Status Badge -->
          <span v-if="props.column.field === 'status'">
            <div class="d-flex flex-column">
              <b-badge
                :variant="getStatusVariant(props.row.status)"
                class="mb-1"
              >
                {{ props.row.status_display || formatStatus(props.row.status) }}
              </b-badge>
              <small
                v-if="props.row.status === 'confirmed' && props.row.confirmed_at"
                class="text-muted"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  size="12"
                  class="mr-1"
                />
                {{ formatDate(props.row.confirmed_at) }}
              </small>
              <small
                v-else-if="props.row.status === 'cancelled' && props.row.cancelled_at"
                class="text-muted"
              >
                <feather-icon
                  icon="SlashIcon"
                  size="12"
                  class="mr-1"
                />
                {{ formatDate(props.row.cancelled_at) }}
              </small>
            </div>
          </span>

          <!-- Invoice ID with QR Code -->
          <div
            v-else-if="props.column.field === 'id'"
            class="d-flex align-items-center"
          >
            <qrcode-vue
              :value="props.row.id.toString()"
              :size="40"
              level="H"
              class="mr-2"
            />
            <div>
              <strong class="text-primary">#{{ props.row.id }}</strong>
              <br>
              <small class="text-muted">{{ formatDate(props.row.created_at) }}</small>
            </div>
          </div>

          <!-- Supplier with Avatar (only show username, not full object) -->
          <span v-else-if="props.column.field === 'supplier'">
            <div
              v-if="user.role !== 'supplier'"
              class="d-flex align-items-center"
            >
              <b-avatar
                :text="getInitials(props.row.supplier && props.row.supplier ? props.row.supplier : '')"
                variant="light-primary"
                size="sm"
                class="mr-2"
              />
              <div>
                <strong class="font-weight-bold">{{ props.row.supplier && props.row.supplier ? props.row.supplier : 'N/A' }}</strong>
                <br>
                <small class="text-muted">{{ props.row.repository ? props.row.repository : 'N/A' }}</small>
              </div>
            </div>
          </span>

          <!-- Select Checkbox -->
          <span v-else-if="props.column.field === 'select'">
            <b-form-checkbox
              :checked="selectedInvoiceIds.includes(props.row.id)"
              class="mb-0"
              @change="toggleInvoiceSelection(props.row.id, $event)"
            />
          </span>

          <!-- Order Summary -->
          <span v-else-if="props.column.field === 'orders_summary'">
            <div class="d-flex flex-column">
              <div class="d-flex align-items-center mb-1">
                <b-badge
                  variant="success"
                  pill
                  class="mr-1"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="12"
                    class="mr-1"
                  />
                  {{ props.row.nb_delivered ? props.row.nb_delivered : 0 }}
                </b-badge>
                <span class="font-weight-bold text-success">livrés</span>
              </div>
              <div class="d-flex align-items-center">
                <b-badge
                  variant="warning"
                  pill
                  class="mr-1"
                >
                  <feather-icon
                    icon="RotateCcwIcon"
                    size="12"
                    class="mr-1"
                  />
                  {{ props.row.nb_return ? props.row.nb_return : 0 }}
                </b-badge>
                <span class="font-weight-bold text-warning">retours</span>
              </div>
            </div>
          </span>

          <!-- Total TTC -->
          <span v-else-if="props.column.field === 'total_ttc'">
            <div class="text-right">
              <strong class="text-dark font-weight-bold">{{ formatPrice(props.row.total_ttc) }} TND</strong>
            </div>
          </span>

          <!-- Total Delivery -->
          <span v-else-if="props.column.field === 'total_delivery'">
            <div class="text-right">
              <strong class="text-dark font-weight-bold">{{ formatPrice(props.row.total_delivery) }} TND</strong>
            </div>
          </span>

          <!-- Total HT -->
          <span v-else-if="props.column.field === 'total'">
            <div class="text-right">
              <strong class="text-dark font-weight-bold">{{ formatPrice(props.row.total) }} TND</strong>
            </div>
          </span>

          <!-- Frais Société -->
          <span v-else-if="props.column.field === 'frais_societe_livraison'">
            <div class="text-right">
              <strong class="text-warning font-weight-bold">{{ formatPrice(props.row.frais_societe_livraison) }} TND</strong>
            </div>
          </span>

          <!-- Net à Payer -->
          <span v-else-if="props.column.field === 'net_a_payer'">
            <div class="text-right">
              <strong class="text-success font-weight-bold">{{ formatPrice(props.row.net_a_payer) }} TND</strong>
            </div>
          </span>

          <!-- Payment Status -->
          <span v-else-if="props.column.field === 'payment_status'">
            <div>
              <b-badge
                :variant="props.row.is_payment_validated ? 'success' : 'warning'"
                class="mb-1"
              >
                <feather-icon
                  :icon="props.row.is_payment_validated ? 'CheckCircleIcon' : 'ClockIcon'"
                  size="12"
                  class="mr-1"
                />
                {{ props.row.is_payment_validated ? 'Paiement Validé' : 'En attente' }}
              </b-badge>
              <div
                v-if="props.row.is_payment_validated"
                class="mt-1"
              >
                <small
                  v-if="props.row.payment_validated_at"
                  class="text-muted d-block"
                >
                  <feather-icon
                    icon="CalendarIcon"
                    size="12"
                    class="mr-1"
                  />
                  {{ formatDate(props.row.payment_validated_at) }}
                </small>
                <small
                  v-if="getValidatorDisplayName(props.row)"
                  class="text-muted d-block font-weight-bold"
                >
                  <feather-icon
                    icon="UserCheckIcon"
                    size="12"
                    class="mr-1 text-primary"
                  />
                  Confirmé par: {{ getValidatorDisplayName(props.row) }}
                </small>
              </div>
            </div>
          </span>

          <!-- Actions -->
          <span v-else-if="props.column.field === 'action'">
            <div class="d-flex justify-content-center">
              <b-button
                :to="{ name: 'invoice-show', params: { id: props.row.id } }"
                variant="outline-primary"
                size="sm"
                class="mr-1"
              >
                <feather-icon
                  icon="EyeIcon"
                  size="14"
                />
              </b-button>

              <!-- Per-row CSV export button -->
              <b-button
                v-b-tooltip.hover.top="'Exporter commandes CSV'"
                variant="outline-info"
                size="sm"
                class="mr-1"
                :disabled="exportingPaymentIds.includes(props.row.id)"
                @click="handleExportPaymentCSV(props.row.id)"
              >
                <b-spinner
                  v-if="exportingPaymentIds.includes(props.row.id)"
                  small
                  type="border"
                />
                <feather-icon
                  v-else
                  icon="DownloadIcon"
                  size="14"
                />
              </b-button>

              <!-- Validate Payment button for allowed roles (Supplier, Admin, Superadmin, Accountant) -->
              <b-button
                v-if="canValidatePayment(props.row)"
                v-b-tooltip.hover.top="user.role === 'supplier' ? 'Confirmer la réception du paiement' : 'Valider le paiement de la facture fournisseur'"
                size="sm"
                variant="success"
                class="supplier-table-validate-btn"
                :disabled="props.row._isValidating"
                @click="confirmValidatePayment(props.row)"
              >
                <b-spinner
                  v-if="props.row._isValidating"
                  small
                  type="border"
                  class="mr-1"
                />
                <feather-icon
                  v-else
                  icon="CheckCircleIcon"
                  size="14"
                  class="mr-1"
                />
                Valider paiement
              </b-button>

              <!-- Admin: confirm draft invoice -->
              <b-button
                v-else-if="user.role === 'admin' && props.row.status === 'draft'"
                size="sm"
                variant="success"
                :disabled="props.row._isConfirming"
                @click="confirmInvoice(props.row)"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="14"
                  class="mr-1"
                />
                Confirmer
              </b-button>

              <!-- Admin: cancel confirmed invoice same day -->
              <b-button
                v-else-if="user.role === 'admin' && props.row.status === 'confirmed'"
                size="sm"
                variant="danger"
                :disabled="props.row._isCancelling || !props.row.can_cancel"
                @click="cancelInvoice(props.row)"
              >
                <feather-icon
                  icon="SlashIcon"
                  size="14"
                  class="mr-1"
                />
                Annuler
              </b-button>
            </div>
          </span>

          <!-- Default -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>

      <!-- Custom Pagination -->
      <b-card-footer class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <span class="text-muted mr-2">Afficher</span>
          <b-form-select
            v-model="pageLength"
            :options="pageLengthOptions"
            size="sm"
            style="width: auto;"
            @change="changePageLength"
          />
          <span class="text-muted ml-2">de {{ count }} entrées</span>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="count"
          :per-page="pageLength"
          :limit="5"
          size="sm"
          class="mb-0"
          @change="pageNext"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="14"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="14"
            />
          </template>
        </b-pagination>
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import {
  BAvatar, BPagination, BFormSelect, BSpinner, BFormGroup, BButton,
  BCol, BRow, BCard, BCardBody, BCardFooter, BBadge, BFormInput,
  BFormDatepicker, BAlert, BSidebar, BButtonGroup, BFormCheckbox,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import storeAuth from '@/store/store'
import QrcodeVue from 'qrcode.vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import exportPaymentCSV from '@/utils/exportPaymentCSV'

export default {
  components: {
    VueGoodTable,
    BAvatar,
    BPagination,
    BFormGroup,
    BFormSelect,
    BButton,
    BSpinner,
    QrcodeVue,
    vSelect,
    BCol,
    BRow,
    BCard,
    BCardBody,
    BCardFooter,
    BBadge,
    BFormInput,
    BFormDatepicker,
    BAlert,
    BSidebar,
    BButtonGroup,
    BFormCheckbox,
  },
  data() {
    return {
      currentPage: 1,
      count: 0,
      size: 40,
      isLoading: false,
      pageLength: 50,
      pageLengthOptions: [
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
        { value: 200, text: '200' },
      ],
      columns: [],
      invoices: [],
      user: {},
      selectedInvoiceIds: [],
      isExporting: false,
      exportingPaymentIds: [],
      searchTerm: '',
      suppliers: [],
      repositories: [],
      filters: {
        reference: '',
        supplier: null,
        repository: null,
        dateFrom: '',
        dateTo: '',
        totalMin: '',
        totalMax: '',
        deliveredMin: '',
      },
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1, // Monday
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
        labelCalendar: 'Calendrier',
        labelNav: 'Navigation du calendrier',
      },
      invoiceStats: null,
      invoiceStatsLoading: false,
      invoiceStatsError: null,
      showStatsSection: false,
      selectedTimeframe: 'today',
      timeframeOptions: [
        { value: 'today', label: 'Aujourd\'hui' },
        { value: 'this_week', label: 'Cette semaine' },
        { value: 'this_month', label: 'Ce mois' },
      ],
    }
  },
  computed: {
    tableColumns() {
      if (this.user.role === 'admin') {
        return this.columns
      } if (this.user.role === 'manager') {
        return this.columns.filter(column => !['repository'].includes(column.field))
      } if (this.user.role === 'supplier') {
        return this.columns.filter(column => !['supplier'].includes(column.field))
      }
      return this.columns
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(filter => filter !== null && filter !== '' && filter !== undefined)
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter(filter => filter !== null && filter !== '' && filter !== undefined).length
    },
    pendingSupplierInvoices() {
      if (this.user.role !== 'supplier') return []
      return this.invoices.filter(invoice => !invoice.is_payment_validated)
    },
    pendingSupplierNetTotal() {
      return this.pendingSupplierInvoices.reduce((sum, invoice) => {
        const rawValue = invoice.net_a_payer !== null && invoice.net_a_payer !== undefined
          ? invoice.net_a_payer
          : invoice.total || 0
        const numericValue = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue
        return sum + (Number.isNaN(numericValue) ? 0 : Number(numericValue || 0))
      }, 0)
    },
    showLeaderboards() {
      return this.user.role === 'admin' || this.user.role === 'manager'
    },
    selectedStats() {
      const base = {
        overview: {
          total_invoices: 0,
          total_ttc: 0,
          total_ht: 0,
          total_delivery: 0,
          delivered_orders: 0,
          return_orders: 0,
          return_rate: 0,
        },
        payment_status: {
          validated_count: 0,
          pending_count: 0,
          validation_rate: 0,
          avg_validation_delay_days: 0,
          outstanding_total_ttc: 0,
        },
        financials: {
          average_invoice_value: 0,
          average_margin_rate: 0,
          average_order_value: 0,
        },
        top_suppliers: [],
        top_repositories: [],
        recent_invoices: [],
      }

      if (!this.invoiceStats) {
        return base
      }

      const source = (this.invoiceStats.timeframes && this.invoiceStats.timeframes[this.selectedTimeframe]) || this.invoiceStats

      return {
        overview: { ...base.overview, ...(source.overview || {}) },
        payment_status: { ...base.payment_status, ...(source.payment_status || {}) },
        financials: { ...base.financials, ...(source.financials || {}) },
        top_suppliers: source.top_suppliers || [],
        top_repositories: source.top_repositories || [],
        recent_invoices: source.recent_invoices || [],
      }
    },
  },
  created() {
    this.user = storeAuth.state.user

    // Setup columns based on user role
    this.setupColumns()

    this.loadInitialData()
  },
  methods: {
    /**
     * Load initial data for the page
     */
    async loadInitialData() {
      try {
        const promises = [this.getInvoices(1)]
        if (this.showStatsSection) {
          promises.push(this.loadInvoiceStatistics())
        }

        // Only fetch suppliers for admin and manager users
        if (this.user.role === 'admin' || this.user.role === 'manager') {
          promises.push(this.getSuppliers())
        }

        // Only fetch repositories for admin users
        if (this.user.role === 'admin') {
          promises.push(this.getRepositories())
        }

        await Promise.all(promises)
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des données initiales')
      }
    },

    async loadInvoiceStatistics() {
      this.invoiceStatsLoading = true
      this.invoiceStatsError = null
      try {
        const params = this.buildFilterParams()
        const { data } = await axios.get('api/invoices/statistics/', { params })
        this.invoiceStats = data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching invoice statistics:', error)
        this.invoiceStats = null
        this.invoiceStatsError = 'Impossible de charger les statistiques pour le moment.'
      } finally {
        this.invoiceStatsLoading = false
      }
    },

    formatStatsCurrency(value) {
      if (value === null || value === undefined) return '0.000 DT'
      const numeric = typeof value === 'string' ? parseFloat(value) : value
      return `${(numeric || 0).toFixed(3)} DT`
    },
    openInvoiceStatistics() {
      this.showStatsSection = true
      if (!this.invoiceStats && !this.invoiceStatsLoading) {
        this.loadInvoiceStatistics()
      }
    },
    /**
     * Setup columns based on user role
     */
    setupColumns() {
      // Base columns that all users see
      const baseColumns = [
        { label: '', field: 'select', width: '48px' },
        { label: 'Facture', field: 'id', width: '180px' },
      ]

      // Conditionally add supplier column (only for non-supplier users)
      if (this.user.role !== 'supplier') {
        baseColumns.push({
          label: 'Fournisseur',
          field: 'supplier',
          width: '200px',
        })
      }

      // Add remaining columns
      const remainingColumns = [
        { label: 'Statut', field: 'status', width: '140px' },
        { label: 'Colis', field: 'orders_summary', width: '120px' },
        { label: 'Total TTC', field: 'total_ttc', width: '120px' },
        { label: 'Livraison', field: 'total_delivery', width: '120px' },
        { label: 'Total HT', field: 'total', width: '120px' },
        { label: 'Frais', field: 'frais_societe_livraison', width: '120px' },
        { label: 'Net à payer', field: 'net_a_payer', width: '130px' },
        { label: 'Statut Paiement', field: 'payment_status', width: '150px' },
        { label: 'Actions', field: 'action', width: '150px' },
      ]

      // Set the columns
      this.columns = [...baseColumns, ...remainingColumns]
    },
    // Debounced reference search
    debouncedReferenceSearch(value) {
      this.filters.reference = value
      this.applyFilters()
    },
    async getSuppliers() {
      try {
        const { data } = await axios.get('api/auth/users/suppliers/')
        this.suppliers = Array.isArray(data) ? data : []
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des fournisseurs')
      }
    },
    async getRepositories() {
      try {
        const { data } = await axios.get('api/repositories/')
        this.repositories = data.results || data
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des dépôts')
      }
    },
    formatPrice(price) {
      // Handle both string and number values from API
      const numericValue = typeof price === 'string' ? parseFloat(price) : price
      return numericValue && !Number.isNaN(numericValue) ? numericValue.toFixed(3) : '0.000'
    },
    formatStatus(status) {
      if (!status) return 'Inconnu'
      const map = {
        draft: 'Brouillon',
        confirmed: 'Confirmée',
        cancelled: 'Annulée',
      }
      return map[status] || status
    },
    getStatusVariant(status) {
      switch (status) {
        case 'confirmed':
          return 'success'
        case 'cancelled':
          return 'danger'
        case 'draft':
        default:
          return 'secondary'
      }
    },
    formatPercentage(value) {
      // Handle both string and number values from API
      const numericValue = typeof value === 'string' ? parseFloat(value) : value
      return numericValue && !Number.isNaN(numericValue) ? numericValue.toFixed(1) : '0.0'
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    },
    getInitials(name) {
      if (!name) return 'U'
      return name.charAt(0).toUpperCase()
    },
    getProfitVariant(margin) {
      // Convert to number if it's a string
      const numericMargin = typeof margin === 'string' ? parseFloat(margin) : margin
      if (numericMargin >= 20) return 'success'
      if (numericMargin >= 10) return 'warning'
      return 'danger'
    },
    async applyFilters() {
      this.currentPage = 1
      const tasks = [this.getInvoices(1)]
      if (this.showStatsSection) {
        tasks.push(this.loadInvoiceStatistics())
      }
      await Promise.all(tasks)
    },
    async clearFilters() {
      this.filters = {
        reference: '',
        supplier: null,
        repository: null,
        dateFrom: '',
        dateTo: '',
        totalMin: '',
        totalMax: '',
        deliveredMin: '',
      }
      this.currentPage = 1
      const tasks = [this.getInvoices(1)]
      if (this.showStatsSection) {
        tasks.push(this.loadInvoiceStatistics())
      }
      await Promise.all(tasks)
    },
    async refreshData() {
      const tasks = [this.getInvoices(this.currentPage)]
      if (this.showStatsSection) {
        tasks.push(this.loadInvoiceStatistics())
      }
      await Promise.all(tasks)
    },
    async changePageLength() {
      this.currentPage = 1
      const tasks = [this.getInvoices(1)]
      if (this.showStatsSection) {
        tasks.push(this.loadInvoiceStatistics())
      }
      await Promise.all(tasks)
    },
    buildFilterParams() {
      const params = { p: this.currentPage }

      if (this.filters.reference) {
        params.id = this.filters.reference
      }
      if (this.filters.supplier) {
        params.supplier = this.filters.supplier.id
      }
      if (this.filters.repository) {
        params.repository = this.filters.repository.id
      }
      if (this.filters.dateFrom) {
        params.created_at__gte = this.filters.dateFrom
      }
      if (this.filters.dateTo) {
        params.created_at__lte = this.filters.dateTo
      }
      if (this.filters.totalMin) {
        params.total__gte = this.filters.totalMin
      }
      if (this.filters.totalMax) {
        params.total__lte = this.filters.totalMax
      }
      if (this.filters.deliveredMin) {
        params.nb_delivered__gte = this.filters.deliveredMin
      }

      return params
    },
    async pageNext(currentPage) {
      this.currentPage = currentPage
      const tasks = [this.getInvoices(currentPage)]
      if (this.showStatsSection) {
        tasks.push(this.loadInvoiceStatistics())
      }
      await Promise.all(tasks)
    },
    async getInvoices(currentPage) {
      this.isLoading = true
      try {
        const params = this.buildFilterParams()
        params.p = currentPage
        params.page_size = this.pageLength

        const { data } = await axios.get('api/invoices/', { params })
        this.count = data.count
        this.invoices = data.results
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des données')
      } finally {
        this.isLoading = false
      }
    },
    async confirmInvoice(invoice) {
      this.$set(invoice, '_isConfirming', true)
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Confirmer la facture #${invoice.id} ?\n\nCette action marquera les commandes comme payées.`,
          {
            title: 'Confirmer la facture',
            size: 'sm',
            okVariant: 'success',
            okTitle: 'Confirmer',
            cancelTitle: 'Annuler',
            footerClass: 'p-2',
            centered: true,
          },
        )

        if (!confirmed) {
          return
        }

        const { data } = await axios.post('api/invoices/confirm/', { invoice_id: invoice.id })
        this.updateInvoiceInList(data)
        this.showToast('success', 'top-right', 'Facture confirmée avec succès')
      } catch (error) {
        const message = error.response?.data?.error || 'Impossible de confirmer la facture'
        this.showToast('danger', 'top-right', message)
      } finally {
        this.$set(invoice, '_isConfirming', false)
      }
    },
    async cancelInvoice(invoice) {
      this.$set(invoice, '_isCancelling', true)
      try {
        const reason = await this.$bvModal.msgBoxPrompt(
          `Vous êtes sur le point d'annuler la facture confirmée #${invoice.id}.\n\nCette opération est autorisée uniquement le jour de la confirmation et rétablira les statuts des commandes.\n\nMotif d'annulation (optionnel):`,
          {
            title: 'Annuler la facture',
            size: 'sm',
            okVariant: 'danger',
            okTitle: 'Annuler la facture',
            cancelTitle: 'Retour',
            footerClass: 'p-2',
            centered: true,
            hideHeaderClose: false,
            inputAttrs: {
              maxlength: 255,
            },
          },
        )

        if (reason === null) {
          return
        }

        const { data } = await axios.post('api/invoices/cancel/', {
          invoice_id: invoice.id,
          reason: reason || '',
        })
        this.updateInvoiceInList(data)
        this.showToast('success', 'top-right', 'Facture annulée avec succès')
      } catch (error) {
        const message = error.response?.data?.error || 'Impossible d\'annuler la facture'
        this.showToast('danger', 'top-right', message)
      } finally {
        this.$set(invoice, '_isCancelling', false)
      }
    },
    updateInvoiceInList(updatedInvoice) {
      const idx = this.invoices.findIndex(inv => inv.id === updatedInvoice.id)
      if (idx !== -1) {
        this.$set(this.invoices, idx, { ...this.invoices[idx], ...updatedInvoice })
      }
    },
    async exportData() {
      try {
        if (this.selectedInvoiceIds.length === 0) {
          this.showToast('warning', 'top-right', 'Sélectionnez au moins une facture')
          return
        }

        this.isExporting = true
        this.showToast('info', 'top-right', 'Export en cours...')

        const { data } = await axios.post(
          'api/invoices/export-csv/',
          { invoice_ids: this.selectedInvoiceIds },
          { responseType: 'blob' },
        )

        const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        const today = new Date().toISOString().slice(0, 10)
        link.href = url
        link.setAttribute('download', `factures_${today}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        this.showToast('success', 'top-right', 'Export terminé')
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors de l\'export')
      } finally {
        this.isExporting = false
      }
    },
    toggleInvoiceSelection(id, checked) {
      if (checked) {
        if (!this.selectedInvoiceIds.includes(id)) {
          this.selectedInvoiceIds.push(id)
        }
      } else {
        this.selectedInvoiceIds = this.selectedInvoiceIds.filter(item => item !== id)
      }
    },
    selectAllVisible() {
      const ids = this.invoices.map(inv => inv.id)
      const selected = new Set(this.selectedInvoiceIds)
      ids.forEach(id => selected.add(id))
      this.selectedInvoiceIds = Array.from(selected)
    },
    clearSelection() {
      this.selectedInvoiceIds = []
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    clearDateFilter(field) {
      this.filters[field] = ''
      this.applyFilters()
    },
    /**
     * Handle per-row CSV export for a specific payment (invoice).
     * Shows a loading spinner on the row button, triggers the download,
     * and displays a success or error toast.
     *
     * @param {number} paymentId - The invoice/payment ID to export
     */
    async handleExportPaymentCSV(paymentId) {
      // Add to loading set
      this.exportingPaymentIds.push(paymentId)
      try {
        await exportPaymentCSV(paymentId)
        this.showToast('success', 'top-right', `Export CSV de la facture #${paymentId} terminé`)
      } catch (error) {
        const message = error.message || 'Erreur lors de l\'export CSV'
        this.showToast('danger', 'top-right', message)
      } finally {
        this.exportingPaymentIds = this.exportingPaymentIds.filter(id => id !== paymentId)
      }
    },
    canValidatePayment(invoice) {
      if (!invoice || invoice.is_payment_validated) return false
      if (invoice.status !== 'confirmed') return false
      const allowedRoles = ['admin', 'superadmin', 'accountant', 'supplier']
      const userRole = (this.user && this.user.role) || ''
      if (allowedRoles.includes(userRole)) return true
      if (this.user && this.user.groups && Array.isArray(this.user.groups)) {
        return this.user.groups.some(g => allowedRoles.includes(typeof g === 'string' ? g : g.name))
      }
      return false
    },
    getValidatorDisplayName(invoice) {
      if (!invoice || !invoice.is_payment_validated) return null
      const name = invoice.payment_validated_by_name || invoice.payment_validated_by
      if (!name) return null
      const roleKey = (invoice.payment_validated_by_role || '').toLowerCase()
      const roleMap = {
        admin: 'Admin',
        superadmin: 'Superadmin',
        accountant: 'Comptable',
        supplier: 'Fournisseur',
        manager: 'Gestionnaire',
      }
      const roleLabel = roleMap[roleKey] || roleKey
      return roleLabel ? `${name} (${roleLabel})` : name
    },
    async confirmValidatePayment(invoice) {
      try {
        const deliveryAmount = (parseFloat(invoice.total_delivery) || 0).toFixed(3)
        const isSupplier = this.user && this.user.role === 'supplier'
        const promptNote = isSupplier
          ? 'Confirmez uniquement si vous avez bien reçu le paiement. Cette validation est définitive.'
          : "Confirmez-vous la validation du paiement pour cette facture fournisseur au nom de l'entreprise ?"

        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Facture #${invoice.id}

Colis livrés: ${invoice.nb_delivered || 0}
Colis retournés: ${invoice.nb_return || 0}
Total TTC: ${this.formatPrice(invoice.total_ttc)} TND
Frais livraison: ${deliveryAmount} TND
Net à payer: ${this.formatPrice(invoice.net_a_payer || invoice.total)} TND

${promptNote}`,
          {
            title: isSupplier ? 'Valider le paiement reçu' : 'Valider le paiement de la facture',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'success',
            okTitle: 'Oui, valider le paiement',
            cancelVariant: 'outline-secondary',
            cancelTitle: 'Annuler',
            footerClass: 'p-3 border-top',
            headerClass: 'p-3 border-bottom bg-light',
            bodyClass: 'p-3 supplier-payment-confirmation-body',
            hideHeaderClose: false,
            centered: true,
            noCloseOnBackdrop: true,
            noCloseOnEsc: true,
            modalClass: 'payment-confirmation-modal',
          },
        )

        if (confirmed) await this.validatePayment(invoice)
      } catch (error) {
        // User cancelled or closed dialog - do nothing
      }
    },
    async validatePayment(invoice) {
      // Optimistic UI: set loading state on the row
      this.$set(invoice, '_isValidating', true)
      const isSupplier = this.user && this.user.role === 'supplier'
      const endpoint = isSupplier
        ? 'api/invoices/validate-payment/'
        : 'api/invoices/admin-validate-payment/'

      try {
        const response = await axios.patch(endpoint, {
          invoice_id: invoice.id,
        })
        if (response.data && response.data.success) {
          // Update the invoice in the list
          const idx = this.invoices.findIndex(inv => inv.id === invoice.id)
          if (idx !== -1) {
            this.$set(this.invoices, idx, {
              ...this.invoices[idx],
              is_payment_validated: true,
              payment_validated_at: response.data.payment_validated_at,
              payment_validated_by: response.data.payment_validated_by,
              payment_validated_by_name: response.data.payment_validated_by_name,
              payment_validated_by_role: response.data.payment_validated_by_role,
              _isValidating: false,
            })
          }
          this.showToast('success', 'top-right', response.data.message || 'Paiement de facture validé avec succès.')
        } else {
          this.showToast('danger', 'top-right', response.data.message || 'Erreur lors de la validation.')
        }
      } catch (error) {
        let msg = 'Erreur lors de la validation du paiement.'
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message
        }
        this.showToast('danger', 'top-right', msg)
      } finally {
        // Always clear loading state
        const idx = this.invoices.findIndex(inv => inv.id === invoice.id)
        if (idx !== -1) {
          this.$set(this.invoices[idx], '_isValidating', false)
        }
      }
    },
  },
}
</script>

<style lang="scss">
// Remove scoped and use component-specific selectors
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';

.invoices-index-page {
  // Compact filter styling
  .filter-section {
    .form-group {
      margin-bottom: 1rem;
    }

    .col-lg-3, .col-md-6 {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
  }

  .kpi-tile {
    border-radius: 0.75rem;
    padding: 1.2rem;
    border: 1px solid rgba(115, 103, 240, 0.05);
    box-shadow: 0 0.125rem 0.75rem rgba(0, 0, 0, 0.05);

    .kpi-label {
      margin-bottom: 0.25rem;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      color: #6b7280;
    }

    .kpi-value {
      margin-bottom: 0;
      font-weight: 700;
      color: #1f2937;
    }

    .kpi-icon {
      font-size: 28px;
    }
  }

  .leaderboard-row,
  .recent-invoice-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #f0f2f5;

    &:last-child {
      border-bottom: none;
    }
  }

  .supplier-validation-alert {
    border: 0;
    border-left: 4px solid #ff9f43;
    border-radius: 0.5rem;

    .alert {
      margin-bottom: 0;
    }
  }

  .supplier-validation-alert__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    .btn {
      min-height: 38px;
      white-space: nowrap;
    }
  }

  .supplier-invoice-cards {
    display: grid;
    gap: 0.85rem;
    margin-bottom: 1rem;
    padding: 0 0.25rem;
  }

  .supplier-invoice-card {
    border: 1px solid #e4e7ec;
    border-left: 4px solid #d0d5dd;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 0 0.125rem 0.5rem rgba(15, 23, 42, 0.06);
    padding: 0.9rem;

    &.is-pending {
      border-left-color: #28c76f;
    }
  }

  .supplier-invoice-card__header,
  .supplier-invoice-card__amount,
  .supplier-invoice-card__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .supplier-invoice-card__header {
    margin-bottom: 0.75rem;

    .invoice-ref {
      color: #114092;
      font-size: 1rem;
    }
  }

  .supplier-invoice-card__amount {
    background: #f7f9fc;
    border-radius: 0.4rem;
    padding: 0.75rem;
    margin-bottom: 0.75rem;

    span {
      color: #667085;
      font-weight: 600;
    }

    strong {
      color: #28c76f;
      font-size: 1.1rem;
      text-align: right;
    }
  }

  .supplier-invoice-card__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.5rem;
    margin-bottom: 0.9rem;

    div {
      background: #fbfcfe;
      border: 1px solid #edf0f5;
      border-radius: 0.4rem;
      padding: 0.55rem;
      min-width: 0;
    }

    span,
    strong {
      display: block;
    }

    span {
      color: #667085;
      font-size: 0.75rem;
      font-weight: 600;
    }

    strong {
      color: #1f2937;
      overflow-wrap: anywhere;
    }
  }

  .supplier-invoice-card__actions {
    .btn {
      flex: 1 1 0;
      min-height: 42px;
      white-space: normal;
    }
  }

  .supplier-table-validate-btn {
    min-height: 34px;
    min-width: 118px;
  }

  // Enhanced styling for no results card
  .no-results-card {
    border: 2px dashed #e3e6f0;
    background-color: #f8f9fc;

    .feather-icon {
      opacity: 0.5;
    }
  }

  // Table styling improvements
  .vue-good-table {
    .font-weight-semibold {
      font-weight: 600;
    }

    .text-right {
      text-align: right;
    }

    .text-center {
      text-align: center;
    }
  }

  // Pagination improvements
  .pagination {
    .page-link {
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
    }
  }

  th {
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
  }

  .vgt-table {
    th {
      background-color: #f8f9fa;
      border-bottom: 2px solid #dee2e6;
      font-weight: 600;
    }
    .striped tbody tr:nth-of-type(odd) {
      td {
        background-color: rgba(0, 0, 0, 0.02);
        vertical-align: middle;
        border-bottom: 1px solid #dee2e6;
      }
    }
  }

  .bg-light-primary {
    .striped tbody tr:nth-of-type(odd) {
      background-color: rgba(115, 103, 240, 0.12) !important;
    }
  }

  .bg-light-success {
    background-color: rgba(40, 199, 111, 0.12) !important;
  }

  .bg-light-info {
    background-color: rgba(0, 207, 232, 0.12) !important;
  }

  .bg-light-warning {
    background-color: rgba(255, 159, 67, 0.12) !important;
  }

  .text-purple {
    color: #7367f0 !important;
  }

  .text-teal {
    color: #00cfe8 !important;
  }

  .border-purple {
    border-color: #7367f0 !important;
  }

  .border-teal {
    border-color: #00cfe8 !important;
  }

  // Payment confirmation modal styling
  .payment-confirmation-modal {
    .modal-dialog {
      max-width: 600px;
    }

    .modal-header {
      background: linear-gradient(135deg, #28c76f 0%, #48da89 100%);
      color: white;
      border-bottom: none;

      .modal-title {
        font-weight: 600;
        font-size: 1.1rem;
      }

      .btn-close {
        color: white;
        opacity: 0.8;

        &:hover {
          opacity: 1;
        }
      }
    }

    .modal-body {
      font-family: inherit;
      font-size: 0.9rem;
      line-height: 1.6;
      background-color: #f8f9fa;
      white-space: pre-line;
      max-height: 62vh;
      overflow-y: auto;

      // Style the emoji and text sections
      strong {
        color: #495057;
      }
    }

    .modal-footer {
      border-top: 2px solid #e9ecef;
      background-color: #ffffff;

      .btn {
        font-weight: 600;
        padding: 0.5rem 1.5rem;
        border-radius: 0.375rem;

        &.btn-success {
          background: linear-gradient(135deg, #28c76f 0%, #48da89 100%);
          border: none;
          box-shadow: 0 2px 4px rgba(40, 199, 111, 0.3);

          &:hover {
            background: linear-gradient(135deg, #20b661 0%, #3bc97a 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(40, 199, 111, 0.4);
          }
        }

        &.btn-outline-secondary {
          border-color: #6c757d;
          color: #6c757d;

          &:hover {
            background-color: #6c757d;
            border-color: #6c757d;
          }
        }
      }
    }
  }

  @media (max-width: 991.98px) {
    .supplier-validation-alert {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .supplier-validation-alert__content {
      align-items: stretch;
      flex-direction: column;

      .btn {
        width: 100%;
      }
    }

    .card-body {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }

    .card-footer {
      align-items: stretch !important;
      flex-direction: column;
      gap: 0.75rem;

      .pagination {
        justify-content: center;
      }
    }
  }

  @media (max-width: 575.98px) {
    .supplier-invoice-card__header,
    .supplier-invoice-card__amount,
    .supplier-invoice-card__actions {
      align-items: stretch;
      flex-direction: column;
    }

    .supplier-invoice-card__amount strong {
      text-align: left;
    }

    .supplier-invoice-card__stats {
      grid-template-columns: 1fr;
    }
  }
}
</style>
